import React, { useEffect, useRef, useState } from 'react';

/**
 * Lichte SVG-grafieken voor Menu 4 (DATA) — zelfde aanpak als VerzuimTrend:
 * tekenen op de echte pixelbreedte, hover-tooltip per kolom/segment.
 * Kleuren: vaste categorische volgorde (blauw, oranje, aqua, geel, magenta,
 * groen, violet, rood); de drie cashflow-stromen hebben een vaste kleur.
 */

export const REEKS_KLEUREN = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
export const KLEUR = { blauw: '#2a78d6', rood: '#e34948', oranje: '#eb6834', grid: '#e5e7eb', as: '#6b7280' };

const MAAND_KORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
export const maandLabel = (m: string) => {
  const [j, mm] = m.split('-').map(Number);
  return `${MAAND_KORT[mm - 1]} '${String(j).slice(2)}`;
};
export const euro = (v: number | null | undefined, dec = 0) =>
  v == null ? '—' : `€ ${v.toLocaleString('nl-NL', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
export const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;

export function useBreedte(min = 280) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(640);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const meet = () => setW(Math.max(min, Math.round(el.clientWidth)));
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, [min]);
  return { ref, w };
}

/** Nette as-schaal: 0 .. mooi rond maximum. */
export function niceMax(v: number) {
  if (v <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / exp;
  const stap = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return stap * exp;
}

export const Tooltip: React.FC<{ x: number; w: number; children: React.ReactNode }> = ({ x, w, children }) => (
  <div
    className="pointer-events-none absolute top-1 z-10 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
    style={{ left: Math.min(Math.max(x - 90, 0), w - 180), width: 180 }}
  >
    {children}
  </div>
);

// ─── Trend (single line) / combinatie (lijn + staven, twee assen) ───────────

export const TrendChart: React.FC<{
  maanden: string[];
  lijn: (number | null)[];
  lijnLabel: string;
  staven?: number[];
  stavenLabel?: string;
  hoogte?: number;
}> = ({ maanden, lijn, lijnLabel, staven, stavenLabel, hoogte = 240 }) => {
  const { ref, w: W } = useBreedte();
  const [hover, setHover] = useState<number | null>(null);
  const H = hoogte;
  const combi = Boolean(staven);
  const padL = 48;
  const padR = combi ? 44 : 16;
  const padT = 16;
  const padB = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = Math.max(1, maanden.length);
  const kolom = plotW / n;
  const xMid = (i: number) => padL + kolom * i + kolom / 2;
  const yMaxL = niceMax(Math.max(0.5, ...lijn.filter((v): v is number => v != null)) * 1.15);
  const yMaxR = niceMax(Math.max(1, ...(staven ?? [0])) * 1.15);
  const yL = (v: number) => padT + plotH * (1 - v / yMaxL);
  const yR = (v: number) => padT + plotH * (1 - v / yMaxR);
  const ticks = [0, 0.5, 1];

  const paden: string[] = [];
  let huidig: string[] = [];
  lijn.forEach((v, i) => {
    if (v != null) huidig.push(`${xMid(i)},${yL(v)}`);
    else if (huidig.length) { paden.push(huidig.join(' ')); huidig = []; }
  });
  if (huidig.length) paden.push(huidig.join(' '));
  const staafB = Math.max(4, Math.min(28, kolom * 0.55));
  const labelElke = Math.ceil(n / Math.max(1, Math.floor(plotW / 56)));

  return (
    <div className="relative" ref={ref}>
      <svg width={W} height={H} role="img" aria-label={lijnLabel} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={padT + plotH * (1 - t)} y2={padT + plotH * (1 - t)} stroke={KLEUR.grid} strokeWidth={1} />
            <text x={padL - 6} y={padT + plotH * (1 - t) + 4} textAnchor="end" fontSize={11} fill={combi ? KLEUR.rood : KLEUR.as}>
              {`${(yMaxL * t).toLocaleString('nl-NL', { maximumFractionDigits: 1 })}%`}
            </text>
            {combi && (
              <text x={W - padR + 6} y={padT + plotH * (1 - t) + 4} fontSize={11} fill={KLEUR.blauw}>
                {Math.round(yMaxR * t)}
              </text>
            )}
          </g>
        ))}
        {staven?.map((v, i) =>
          v > 0 ? (
            <rect key={i} x={xMid(i) - staafB / 2} y={yR(v)} width={staafB} height={Math.max(1, padT + plotH - yR(v))} rx={3} fill={KLEUR.blauw} opacity={hover === null || hover === i ? 0.85 : 0.45} />
          ) : null
        )}
        {paden.map((pts, i) => (
          <polyline key={i} points={pts} fill="none" stroke={combi ? KLEUR.rood : KLEUR.blauw} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {lijn.map((v, i) =>
          v != null ? <circle key={i} cx={xMid(i)} cy={yL(v)} r={hover === i ? 5 : 3.5} fill="#fff" stroke={combi ? KLEUR.rood : KLEUR.blauw} strokeWidth={2} /> : null
        )}
        {maanden.map((m, i) =>
          i % labelElke === 0 ? (
            <text key={m} x={xMid(i)} y={H - 8} textAnchor="middle" fontSize={11} fill={KLEUR.as}>
              {maandLabel(m)}
            </text>
          ) : null
        )}
        {hover !== null && <line x1={xMid(hover)} x2={xMid(hover)} y1={padT} y2={padT + plotH} stroke="#9ca3af" strokeDasharray="3 3" />}
        {maanden.map((m, i) => (
          <rect key={`hit-${m}`} x={padL + kolom * i} y={padT} width={kolom} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={0} />
        ))}
      </svg>
      {hover !== null && (
        <Tooltip x={xMid(hover)} w={W}>
          <p className="font-semibold text-gray-900">{maandLabel(maanden[hover])}</p>
          <p className="text-gray-700">{lijnLabel}: <strong>{pct(lijn[hover])}</strong></p>
          {staven && <p className="text-gray-700">{stavenLabel}: <strong>{staven[hover]}</strong></p>}
        </Tooltip>
      )}
    </div>
  );
};

// ─── Gestapelde staven ───────────────────────────────────────────────────────

export interface StapelReeks {
  key: string;
  label: string;
  kleur: string;
}

export const StackedBarChart: React.FC<{
  maanden: string[];
  reeksen: StapelReeks[];
  waarden: Record<string, number>[];
  formaat?: (v: number) => string;
  hoogte?: number;
}> = ({ maanden, reeksen, waarden, formaat = (v) => euro(v), hoogte = 260 }) => {
  const { ref, w: W } = useBreedte();
  const [hover, setHover] = useState<number | null>(null);
  const H = hoogte;
  const padL = 64;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = Math.max(1, maanden.length);
  const kolom = plotW / n;
  const totalen = waarden.map((w) => reeksen.reduce((s, r) => s + (w[r.key] || 0), 0));
  const yMax = niceMax(Math.max(1, ...totalen) * 1.1);
  const schaal = (v: number) => (plotH * v) / yMax;
  const staafB = Math.max(6, Math.min(40, kolom * 0.6));
  const labelElke = Math.ceil(n / Math.max(1, Math.floor(plotW / 56)));

  return (
    <div className="relative" ref={ref}>
      <svg width={W} height={H} role="img" onMouseLeave={() => setHover(null)}>
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={padT + plotH * (1 - t)} y2={padT + plotH * (1 - t)} stroke={KLEUR.grid} />
            <text x={padL - 6} y={padT + plotH * (1 - t) + 4} textAnchor="end" fontSize={11} fill={KLEUR.as}>{formaat(yMax * t)}</text>
          </g>
        ))}
        {waarden.map((w, i) => {
          let y = padT + plotH;
          const x = padL + kolom * i + (kolom - staafB) / 2;
          const zichtbaar = reeksen.filter((r) => (w[r.key] || 0) > 0);
          return (
            <g key={maanden[i]} opacity={hover === null || hover === i ? 1 : 0.55}>
              {zichtbaar.map((r, j) => {
                const h = schaal(w[r.key]);
                y -= h;
                const boven = j === zichtbaar.length - 1;
                // 2px oppervlak-spleet tussen segmenten; alleen het bovenste segment afgerond
                const hNetto = Math.max(1, h - (boven ? 0 : 2));
                return boven ? (
                  <path key={r.key} d={`M${x},${y + hNetto} v${-(hNetto - 4)} q0,-4 4,-4 h${staafB - 8} q4,0 4,4 v${hNetto - 4} z`} fill={r.kleur} />
                ) : (
                  <rect key={r.key} x={x} y={y + 2} width={staafB} height={hNetto} fill={r.kleur} />
                );
              })}
            </g>
          );
        })}
        {maanden.map((m, i) =>
          i % labelElke === 0 ? (
            <text key={m} x={padL + kolom * i + kolom / 2} y={H - 8} textAnchor="middle" fontSize={11} fill={KLEUR.as}>{maandLabel(m)}</text>
          ) : null
        )}
        {maanden.map((m, i) => (
          <rect key={`hit-${m}`} x={padL + kolom * i} y={padT} width={kolom} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} tabIndex={0} onFocus={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && (
        <Tooltip x={padL + kolom * hover + kolom / 2} w={W}>
          <p className="font-semibold text-gray-900">{maandLabel(maanden[hover])}</p>
          {reeksen.map((r) => (
            <p key={r.key} className="flex items-center justify-between gap-2 text-gray-700">
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: r.kleur }} />{r.label}</span>
              <strong>{formaat(waarden[hover][r.key] || 0)}</strong>
            </p>
          ))}
          <p className="mt-1 border-t border-gray-100 pt-1 text-gray-900">Totaal <strong>{formaat(totalen[hover])}</strong></p>
        </Tooltip>
      )}
    </div>
  );
};

export const Legenda: React.FC<{ items: { label: string; kleur: string }[] }> = ({ items }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
    {items.map((i) => (
      <span key={i.label} className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: i.kleur }} />
        {i.label}
      </span>
    ))}
  </div>
);

// ─── Donut ───────────────────────────────────────────────────────────────────

export const DonutChart: React.FC<{
  segmenten: { label: string; waarde: number; kleur: string }[];
  centrumWaarde: string;
  centrumLabel: string;
}> = ({ segmenten, centrumWaarde, centrumLabel }) => {
  const [hover, setHover] = useState<number | null>(null);
  const R = 80;
  const r = 52;
  const C = 100;
  const totaal = segmenten.reduce((s, x) => s + x.waarde, 0) || 1;
  let hoek = -Math.PI / 2;
  const boog = (a0: number, a1: number) => {
    const groot = a1 - a0 > Math.PI ? 1 : 0;
    const p = (rad: number, a: number) => `${C + rad * Math.cos(a)},${C + rad * Math.sin(a)}`;
    return `M${p(R, a0)} A${R},${R} 0 ${groot} 1 ${p(R, a1)} L${p(r, a1)} A${r},${r} 0 ${groot} 0 ${p(r, a0)} Z`;
  };
  const spleet = segmenten.length > 1 ? 0.012 : 0;
  return (
    <svg viewBox="0 0 200 200" className="h-52 w-52 flex-shrink-0" role="img" aria-label={centrumLabel} onMouseLeave={() => setHover(null)}>
      {segmenten.map((s, i) => {
        const a0 = hoek;
        const a1 = hoek + (s.waarde / totaal) * Math.PI * 2;
        hoek = a1;
        const volledig = segmenten.length === 1;
        return volledig ? (
          <g key={s.label}>
            <circle cx={C} cy={C} r={(R + r) / 2} fill="none" stroke={s.kleur} strokeWidth={R - r} />
          </g>
        ) : (
          <path
            key={s.label}
            d={boog(a0 + spleet, a1 - spleet)}
            fill={s.kleur}
            opacity={hover === null || hover === i ? 1 : 0.45}
            onMouseEnter={() => setHover(i)}
          >
            <title>{`${s.label}: ${s.waarde}`}</title>
          </path>
        );
      })}
      <text x={C} y={C - 2} textAnchor="middle" fontSize={26} fontWeight={700} fill="#111827">
        {hover !== null ? segmenten[hover].waarde : centrumWaarde}
      </text>
      <text x={C} y={C + 18} textAnchor="middle" fontSize={11} fill={KLEUR.as}>
        {hover !== null ? segmenten[hover].label.slice(0, 24) : centrumLabel}
      </text>
    </svg>
  );
};

// ─── Voortgangsbalk ──────────────────────────────────────────────────────────

export const Meter: React.FC<{ waarde: number; max: number; kleur?: string; label?: string }> = ({ waarde, max, kleur = KLEUR.blauw, label }) => {
  const p = max > 0 ? Math.min(100, (waarde / max) * 100) : 0;
  return (
    <div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100" role="progressbar" aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className="h-full rounded-full" style={{ width: `${p}%`, background: kleur }} />
      </div>
    </div>
  );
};
